import React from 'react';
import './TermsOfUseModal.css';

interface TermsOfUseModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const TermsOfUseModal: React.FC<TermsOfUseModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handlePrivacyPolicyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // This could open the privacy policy modal, but for now we'll just prevent default
    // In a real implementation, you might want to coordinate between modals
  };

  return (
    <div className="terms-modal-backdrop" onClick={handleBackdropClick}>
      <div className="terms-modal">
        <div className="terms-modal-header">
          <h2>Terms of Use</h2>
          <button 
            className="terms-modal-close" 
            onClick={onClose}
            aria-label="Close Terms of Use"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        
        <div className="terms-modal-content">
          <div className="terms-section">
            <p><strong>Last Updated:</strong> 10-22-2025</p>
            <p>
              Welcome to <strong>Intelyx AI</strong> ("<strong>Intelyx</strong>," "<strong>we</strong>," "<strong>our</strong>," or "<strong>us</strong>").
              These Terms of Use ("<strong>Terms</strong>") govern your access to and use of our website, applications, and services, including our AI-powered product <strong><span className="spotton-brand-text">Spott-on</span></strong> (collectively, the "<strong>Services</strong>").
            </p>
            <p>
              By accessing or using our Services, you agree to these Terms and our <strong><a href="#" onClick={handlePrivacyPolicyClick} className="terms-link">[Privacy Policy]</a></strong>. If you do not agree, do not use our Services.
            </p>
          </div>

          <div className="terms-section">
            <h3>1. Acceptance of Terms</h3>
            <p>
              By using our website or Services, you confirm that you are at least 18 years of age and have the legal authority to enter into these Terms on behalf of yourself or your organization.
              If you are using the Services on behalf of a company or other legal entity, you represent that you have authority to bind that entity to these Terms.
            </p>
          </div>

          <div className="terms-section">
            <h3>2. Use of the Services</h3>
            
            <h4>2.1 Authorized and Lawful Use</h4>
            <p>
              You may use the Services only for lawful purposes and in accordance with these Terms. You agree not to use the Services in any way that violates any applicable federal, state, local, or international law or regulation.
            </p>

            <h4>2.2 Internal Business Use Only</h4>
            <p>
              The Services and any data, insights, analyses, or other outputs generated through them are provided solely for the <strong>internal business purposes</strong> of the subscribing company or authorized user.
              You may not resell, sublicense, share, distribute, or otherwise make available any portion of the Services or their outputs to any third party, whether for commercial gain or otherwise, without <strong>Intelyx's prior written consent</strong>.
            </p>

            <h4>2.3 Prohibited Conduct</h4>
            <p>You agree not to:</p>
            <ul>
              <li>Interfere with, disrupt, or attempt to gain unauthorized access to any part of the Services, related systems, or networks;</li>
              <li>Circumvent or disable any security or usage controls;</li>
              <li>Use the Services to develop, train, or improve competing artificial intelligence or machine-learning models;</li>
              <li>Input, upload, or share any unlawful, infringing, or harmful content; or</li>
              <li>Use automated tools (such as bots or scrapers) to extract or collect data without authorization.</li>
            </ul>

            <h4>2.4 Fair Use and Technical Limits</h4>
            <p>
              You may not use the Services in a manner that imposes an unreasonable or disproportionate load on our infrastructure. Intelyx reserves the right to enforce usage limits, throttle access, or suspend accounts engaging in excessive or abusive activity inconsistent with normal business use.
            </p>

            <h4>2.5 Suspension or Termination</h4>
            <p>
              We may suspend or terminate access to the Services at any time, with or without notice, if we determine that your use violates these Terms or applicable law, or poses a risk to system security or service integrity.
            </p>
          </div>

          <div className="terms-section">
            <h3>3. Intellectual Property</h3>
            <p>
              All content, materials, and software available through the Services—including trademarks, logos, text, graphics, data, and code—are owned by or licensed to <strong>Intelyx AI</strong> and are protected under copyright, trademark, and other intellectual property laws.
              Except as expressly permitted in writing, you may not copy, reproduce, modify, create derivative works from, distribute, display, or otherwise exploit any part of the Services.
            </p>
          </div>

          <div className="terms-section">
            <h3>4. User Content and Feedback</h3>
            <p>
              If you submit feedback, suggestions, or ideas regarding our products or Services, you grant Intelyx a <strong>worldwide, perpetual, irrevocable, royalty-free license</strong> to use, reproduce, and incorporate such feedback into our products or Services without restriction or compensation to you.
            </p>
          </div>

          <div className="terms-section">
            <h3>5. AI Output Disclaimer and Accuracy of Information</h3>
            <p>
              The Services use artificial intelligence and machine-learning models to generate insights, recommendations, or other outputs. Such outputs are <strong>automatically generated</strong> and may contain <strong>errors, omissions, or inaccuracies</strong>, including potentially fabricated or misleading information ("<strong>hallucinations</strong>").
            </p>
            <p>You acknowledge and agree that:</p>
            <ul>
              <li>You are solely responsible for reviewing and validating any outputs before using or acting upon them; and</li>
              <li><strong>Intelyx makes no representations or warranties</strong> regarding the accuracy, reliability, or suitability of any outputs for your intended purpose.</li>
            </ul>
          </div>

          <div className="terms-section">
            <h3>6. Disclaimers</h3>
            <p>
              The Services are provided on an <strong>"as is"</strong> and <strong>"as available"</strong> basis without warranties of any kind, whether express, implied, or statutory, including but not limited to warranties of <strong>merchantability, fitness for a particular purpose, accuracy, reliability, or non-infringement</strong>.
              Intelyx does not guarantee that the Services will be uninterrupted, error-free, secure, or that any content will be accurate or current.
            </p>
          </div>

          <div className="terms-section">
            <h3>7. Limitation of Liability</h3>
            <p>
              To the maximum extent permitted by law, Intelyx AI and its affiliates, officers, employees, and licensors shall not be liable for any <strong>indirect, incidental, special, consequential, exemplary, or punitive damages</strong>, or for any loss of profits, data, or goodwill, arising out of or in connection with your use of, or reliance on, the Services or their outputs, even if we have been advised of the possibility of such damages.
            </p>
            <p>
              In no event shall Intelyx's total cumulative liability exceed the amount paid (if any) by you for access to the Services during the six (6) months preceding the event giving rise to the claim.
            </p>
          </div>

          <div className="terms-section">
            <h3>8. Third-Party Services</h3>
            <p>
              The Services may integrate with or link to third-party platforms (including but not limited to AWS, Google, or LinkedIn). Intelyx is not responsible for the content, data practices, or availability of these third-party services, and your use of them is governed solely by their respective terms and policies.
            </p>
          </div>

          <div className="terms-section">
            <h3>9. Termination</h3>
            <p>
              We may suspend or terminate your access to the Services, in whole or in part, at any time and for any reason, including violation of these Terms.
              Provisions relating to <strong>Intellectual Property</strong>, <strong>Limitation of Liability</strong>, <strong>Disclaimers</strong>, and <strong>Governing Law</strong> shall survive termination.
            </p>
          </div>

          <div className="terms-section">
            <h3>10. Governing Law</h3>
            <p>
              These Terms are governed by and construed in accordance with the <strong>laws of the State of Illinois, USA</strong>, without regard to its conflict-of-law principles.
              Any dispute arising under or relating to these Terms shall be resolved exclusively in the <strong>state or federal courts located in Cook County, Illinois</strong>, and you consent to their jurisdiction and venue.
            </p>
          </div>

          <div className="terms-section">
            <h3>11. Changes to These Terms</h3>
            <p>
              We may update or revise these Terms from time to time. Updated versions will be posted on this page with a revised "<strong>Last Updated</strong>" date.
              Your continued use of the Services after any changes are posted constitutes your acceptance of the revised Terms.
            </p>
          </div>

          <div className="terms-section">
            <h3>12. Contact Us</h3>
            <p>
              If you have questions about these Terms, please contact us at:<br/>
              📧 <strong><a href="mailto:legal@intelyx.ai" className="terms-link">legal@intelyx.ai</a></strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};